// @ts-check

import React, { useState, useId, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BlockStack,
  Card,
  Text,
  InlineStack,
  Button,
  ProgressBar,
  Box,
  Collapsible,
  Icon,
  List,
  DataTable,
  InlineCode,
  MediaCard,
  VideoThumbnail,
  Modal,
  Banner,
} from "@shopify/polaris";
import { CheckIcon, LogoFacebookIcon } from "@shopify/polaris-icons";
import { useAppBridge } from "@shopify/app-bridge-react";

import { OnboardingStep } from "./OnboardingStep.jsx";

import { useGlobalContext } from "context/GlobalContext";
import { usePlatforms } from "context/PlatformsContext";

import { getFacebookLoginUrl } from "utils/facebook";

import thumbnail from "assets/guides/meta-ad-account/meta-ad-account-setup-thumbnail.png";

import "./ConnectableAccountOnboarding.css";

const INVALID_ADS_PAGE_SIZE = 10;

export const ConnectableAccountOnboarding = () => {
  const shopify = useAppBridge();
  const navigate = useNavigate();
  const accessId = useId();
  const { store, refetchStore, isFetchingStore, isRefetchingStore } = useGlobalContext();
  const {
    setupSteps,
    trackingCodeCompletionCount,
    setTrackingCodeCompletionCount,
    trackingCodeCompletionProgress,
    invalidAds,
    setInvalidAds,
    invalidAdsPage,
    setInvalidAdsPage,
    isFetchingConnectablePlatforms,
    isRefetchingConnectablePlatforms,
  } = usePlatforms();

  const [expanded, setExpanded] = useState(setupSteps.findIndex((item) => !item.complete));
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [createPixelLoading, setCreatePixelLoading] = useState(false);
  const [createPixelError, setCreatePixelError] = useState("");
  const [trackingCodeCompletionLoading, setTrackingCodeCompletionLoading] = useState(false);
  const [trackingCodeCompletionError, setTrackingCodeCompletionError] = useState("");

  const completedItemsLength = setupSteps.filter((item) => item.complete).length;

  const getTrackingCodeCompletion = async () => {
    setTrackingCodeCompletionLoading(true);
    setTrackingCodeCompletionError("");

    const params = {
      platform_key: "facebook",
      page: invalidAdsPage,
      action: "resync",
    };

    return fetch(`/api/connected-platforms/tracking-code-completion?${new URLSearchParams(params).toString()}`, {
      method: "GET",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        console.log("CHECK", response)
        if (response.error) {
          throw new Error(response.error);
        }

        setTrackingCodeCompletionCount(response.data?.completionCount || null);
        setInvalidAds(response.data?.invalidAds || []);
      })
      .catch((error) => {
        setTrackingCodeCompletionError(error.message);
        console.error("Error checking tracking code completion", error);
      })
      .finally(() => {
        setTrackingCodeCompletionLoading(false);
      });
  };

  const refetchTrackingCodeCompletion = () => {
    if (invalidAdsPage > 1) {
      // this will trigger a re-fetch in the useEffect
      setInvalidAdsPage(1);
    } else {
      getTrackingCodeCompletion();
    }
  };

  useEffect(() => {
    getTrackingCodeCompletion();
  }, [invalidAdsPage]);

  useEffect(() => {
    return () => {
      setConnectLoading(false);
    }
  }, []);

  const handleGrantAccess = async () => {
    await shopify.scopes.request(["read_pixels", "write_pixels", "read_customer_events"]);
    refetchStore();
  };

  const handleFacebookLogin = () => {
    setConnectLoading(true);
    const redirectUrl = getFacebookLoginUrl(store.shop, { redirectUrl: "/ad-campaigns" });
    open(redirectUrl, "_blank");
  };

  const handleWatchVideo = () => {
    setVideoModalOpen(true);
  };

  const handleFinish = () => {
    navigate("/ad-campaigns");
  };

  const createWebPixel = async () => {
    setCreatePixelLoading(true);
    setCreatePixelError("");

    return fetch("/api/shop/enable-pixel", {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response) => {
        refetchStore();
      })
      .catch((error) => {
        setCreatePixelError("Error creating pixel");
      })
      .finally(() => {
        setCreatePixelLoading(false);
      });
  };

  let pixelBanner;
  if (createPixelError) {
    pixelBanner = (
      <Banner tone="critical">
        <Text as="p">{createPixelError}</Text>
      </Banner>
    );
  }

  let trackingCodeProgressBar;
  let trackingCodeInstructions;
  let invalidAdsTable;

  if (trackingCodeCompletionCount) {
    if (trackingCodeCompletionCount.totalCount > 0) {
      trackingCodeInstructions = (
        <BlockStack gap="400">
          <Text as="h2" variant="headingSm">
            Instructions
          </Text>

          <MediaCard
            title="How do I add these tracking parameters to my ads?"
            primaryAction={{
              content: "Watch video",
              onAction: handleWatchVideo,
            }}
            description={(
              <BlockStack gap="200">
                <Text as="p">
                  You need to add tracking parameters to the URL of your ads so we can attribute your orders to specific ads.
                </Text>

                <Text as="p">
                  Our short video will show you how to do this.
                </Text>
              </BlockStack>
            )}
          >
            <VideoThumbnail
              videoLength={162}
              thumbnailUrl={thumbnail}
              onClick={handleWatchVideo}
            />
          </MediaCard>

          <List type="number" gap="loose">
            <List.Item>
              <BlockStack gap="200">
                <Text as="p">
                  Watch our short YouTube video to learn how to add the Origin tracking parameters to your ads.
                </Text>
              </BlockStack>
            </List.Item>

            <List.Item>
              <BlockStack gap="200">
                <Text as="p">
                  Visit each of the following pages and use the extension to add the Origin tracking parameters to your ads.
                </Text>
              </BlockStack>
            </List.Item>
          </List>
        </BlockStack>
      )
    }

    trackingCodeProgressBar = (
      <BlockStack gap='400'>
        <InlineStack blockAlign='center' gap='400'>
          <div style={{ width: '100px' }}>
            <ProgressBar
              progress={trackingCodeCompletionProgress}
              size='small'
              tone='highlight'
              animated
            />
          </div>

          <Text as='p' variant='bodySm'>
            {`${trackingCodeCompletionCount.validCount} / ${trackingCodeCompletionCount.totalCount} ads (${Math.round(trackingCodeCompletionProgress)}%) have the correct tracking parameters`}
          </Text>
        </InlineStack>
      </BlockStack>
    );

    invalidAdsTable = (
      <DataTable
        columnContentTypes={[
          "text",
          "text",
          "text",
        ]}
        headings={[
          "Ad",
          "Parameters",
          "Go to page",
        ]}
        rows={invalidAds.map(({ id, ad_name, connected_platform_key, ad_internal_id, ad_manager_url }) => ([
          ad_name,
          <List type="bullet" gap="extraTight">
            <List.Item>
              <InlineCode>
                origin_source: {connected_platform_key}
              </InlineCode>
            </List.Item>
            <List.Item>
              <InlineCode>
                origin_id: {ad_internal_id}
              </InlineCode>
            </List.Item>
          </List>,
          <Button
            variant="plain"
            onClick={() => open(ad_manager_url, "_blank")}
          >
            Go to ad manager
          </Button>
        ]))}
        pagination={{
          hasNext: invalidAdsPage < (trackingCodeCompletionCount?.totalCount || 0) / INVALID_ADS_PAGE_SIZE,
          hasPrevious: invalidAdsPage > 1,
          onNext: () => {
            setInvalidAdsPage((invalidAdsPage) => Math.min(invalidAdsPage + 1, (trackingCodeCompletionCount?.totalCount || 0) / INVALID_ADS_PAGE_SIZE));
          },
          onPrevious: () => {
            setInvalidAdsPage((invalidAdsPage) => Math.max(invalidAdsPage - 1, 1));
          },
        }}
      />
    );
  }

  return (
    <>
      <Modal
        open={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        title="How to add tracking parameters to your ads"
        secondaryActions={[
          {
            content: "Done",
            onAction: () => setVideoModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <iframe width="560" height="315" src="https://www.youtube.com/embed/H8KLN23_tYQ?si=KXYVB7I_3HBu07hy" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
        </Modal.Section>
      </Modal>

      <Card padding="0">
        <Box padding="400">
          <BlockStack>
            <Text as="h2" variant="headingMd">
              Setup Guide
            </Text>

            <Text as="p" variant="bodyMd">
              Use this personalized guide to start syncing your Facebook Ads with Origin.
            </Text>

            <div style={{ marginTop: '.8rem' }}>
              <InlineStack blockAlign='center' gap='200'>
                {completedItemsLength !== setupSteps.length ? (
                  <div style={{ width: '100px' }}>
                    <ProgressBar
                      progress={(completedItemsLength / setupSteps.length) * 100}
                      size='small'
                      tone='primary'
                      animated
                    />
                  </div>
                ) : null}

                {completedItemsLength === setupSteps.length ? (
                  <div style={{ maxHeight: '1rem' }}>
                    <InlineStack wrap={false} gap='100'>
                      <Icon
                        source={CheckIcon}
                        tone='subdued'
                        accessibilityLabel='Check icon to indicate completion of Setup Guide'
                      />
                      <Text as='p' variant='bodySm' tone='subdued'>
                        Done
                      </Text>
                    </InlineStack>
                  </div>
                ) : (
                  <Text as='span' variant='bodySm'>
                      {`${completedItemsLength} / ${setupSteps.length} completed`}
                  </Text>
                )}
              </InlineStack>
            </div>
          </BlockStack>
        </Box>

        <Collapsible open id={accessId}>
          <Box padding="200">
            <BlockStack gap="100">
              <OnboardingStep
                index={0}
                complete={setupSteps[0].complete}
                expanded={expanded === 0}
                loading={isFetchingStore || isRefetchingStore}
                setExpanded={() => setExpanded(0)}
                title="Grant additional access scopes"
                description={
                  <BlockStack gap="200">
                    <Text as="p">
                      We need additonal access to ensure we can accurately track your orders.
                    </Text>

                    <List type="bullet" gap="extraTight">
                      <List.Item>
                        <Text as="p">
                          <InlineCode>read_pixels</InlineCode>: to check if you have added our tracking pixel to your storefront
                        </Text>
                      </List.Item>

                      <List.Item>
                        <Text as="p">
                          <InlineCode>write_pixels</InlineCode>: to automatically add our tracking pixel to your storefront
                        </Text>
                      </List.Item>

                      <List.Item>
                        <Text as="p">
                          <InlineCode>read_customer_events</InlineCode>: to track when people visit your storefront and make a purchase
                        </Text>
                      </List.Item>
                    </List>
                  </BlockStack>
                }
                primaryButton={{
                  content: "Grant additional access",
                  props: {
                    onClick: handleGrantAccess,
                    loading: isFetchingStore || isRefetchingStore,
                    disabled: setupSteps[0].complete,
                  },
                }}
              />

              <OnboardingStep
                index={1}
                complete={setupSteps[1].complete}
                expanded={expanded === 1}
                loading={isFetchingConnectablePlatforms || isRefetchingConnectablePlatforms}
                setExpanded={() => setExpanded(1)}
                title="Link your Facebook account"
                description={
                  <Text as="p">
                    We will request <Text fontWeight="semibold" as="span">read-only</Text> access to your Meta ads account so we can effectively attribute your orders.
                  </Text>
                }
                primaryButton={{
                  content: "Link facebook account",
                  props: {
                    loading: connectLoading,
                    onClick: handleFacebookLogin,
                    icon: LogoFacebookIcon,
                    disabled: setupSteps[1].complete,
                  },
                }}
              />

              <OnboardingStep
                index={2}
                complete={setupSteps[2].complete}
                expanded={expanded === 2}
                setExpanded={() => setExpanded(2)}
                title="Add the Origin pixel to your store"
                description={
                  <BlockStack gap="200">
                    {pixelBanner}

                    <Text as="p">
                      The Origin pixel is used to track when visitors come to your store from external sources.
                    </Text>

                    <Text as="p">
                      If you uninstall Origin it will be automatically removed from your store.
                    </Text>
                  </BlockStack>
                }
                primaryButton={{
                  content: "Add pixel",
                  props: {
                    onClick: createWebPixel,
                    loading: createPixelLoading,
                    disabled: setupSteps[2].complete,
                  },
                }}
              />

              <OnboardingStep
                index={3}
                complete={setupSteps[3].complete}
                expanded={expanded === 3}
                setExpanded={() => setExpanded(3)}
                title="Add the Origin tracking parameters to your ads"
                description={
                  <BlockStack gap="400">
                    {(trackingCodeCompletionError) ? (
                      <Banner tone="critical" title="Having trouble?">
                        <BlockStack gap="200">
                          <Text as="p">Try unlinking your Facebook account and linking it again in the settings.</Text>
                          <Text as="p">You might need to wait a few minitues before trying this again to ensure all your data is synced correctly.</Text>
                        </BlockStack>
                      </Banner>
                    ) : null}

                    <Text as="p">
                      You need to add the following tracking parameters to the URL of your ads. These extra parameters in the URL allow us to attribute orders to specific ads.
                    </Text>

                    {trackingCodeProgressBar}

                    {trackingCodeInstructions}

                    {invalidAdsTable}
                  </BlockStack>
                }
                primaryButton={{
                  content: "Check completion",
                  props: {
                    onClick: refetchTrackingCodeCompletion,
                    loading: trackingCodeCompletionLoading,
                  },
                }}
              />
            </BlockStack>
          </Box>
        </Collapsible>

        {completedItemsLength === setupSteps.length ? (
          <Box
            background='bg-surface-secondary'
            borderBlockStartWidth='025'
            borderColor='border-secondary'
            padding='300'
          >
            <InlineStack align="end">
              <Button onClick={handleFinish}>Finish</Button>
            </InlineStack>
          </Box>
        ) : null}
      </Card>
    </>
  );
};
